import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { apiGet } from '../../contexts/AuthContext';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Search, FileText, Building2, User, Calendar, Filter, ExternalLink, ArrowLeftRight } from 'lucide-react';
import { toast } from 'sonner';
import { ApplicationTicket } from '../../components/passport/PassportPortfolio';

import { APPLICATION_STATUSES as STATUSES, APP_STATUS_STYLES as STATUS_STYLES } from '../../components/WorkflowTracker';

export default function PartnerApplications() {
  const navigate = useNavigate();
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [handoffFilter, setHandoffFilter] = useState('all');
  // Status is filtered here, not on the server, so every status pill keeps its count.
  const shown = statusFilter === 'all' ? applications : applications.filter(a => a.status === statusFilter);

  const [debouncedSearch, setDebouncedSearch] = useState('');

  // Wait for typing to pause before searching.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 350);
    return () => clearTimeout(t);
  }, [search]);

  const fetchApplications = useCallback((signal) => {
    setLoading(true);
    const params = new URLSearchParams();
    if (debouncedSearch) params.set('search', debouncedSearch);
    if (handoffFilter !== 'all') params.set('handoff_state', handoffFilter);
    const qs = params.toString();
    apiGet(`/applications${qs ? '?' + qs : ''}`, { signal })
      .then(r => setApplications(r.data))
      .catch(err => { if (err.code !== 'ERR_CANCELED') toast.error('Failed to load applications'); })
      .finally(() => { if (!signal?.aborted) setLoading(false); });
  }, [debouncedSearch, handoffFilter]);

  // A newer search cancels the older request, so a slow stale reply can't overwrite the list.
  useEffect(() => {
    const controller = new AbortController();
    fetchApplications(controller.signal);
    return () => controller.abort();
  }, [fetchApplications]);

  return (
    <div className="space-y-6 animate-fade-in-up">
      <div className="flex items-center gap-4">
        <div>
          <h1 className="text-3xl font-semibold text-ember tracking-tight" style={{ fontFamily: 'Outfit' }}>My Applications</h1>
          <p className="text-ink-2 mt-1">Track college applications and who owns the next step</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1 sm:max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-3" />
          <Input value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search by candidate, university, app number..."
            className="pl-10 h-11 border-contrast/10" />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-full sm:w-48 h-11" aria-label="Filter by status">
            <Filter className="w-4 h-4 mr-2 text-ink-3" />
            <SelectValue placeholder="All Statuses" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            {STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={handoffFilter} onValueChange={setHandoffFilter}>
          <SelectTrigger className="w-full sm:w-56 h-11" aria-label="Filter by handoff">
            <ArrowLeftRight className="w-4 h-4 mr-2 text-ink-3" />
            <SelectValue placeholder="All handoffs" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All handoffs</SelectItem>
            <SelectItem value="preparing">Preparing with partner</SelectItem>
            <SelectItem value="with_team">With XpertAdvisor</SelectItem>
            <SelectItem value="returned">Needs partner input</SelectItem>
            <SelectItem value="untracked">No handoff recorded</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Status summary pills */}
      <div className="flex flex-wrap gap-2">
        {STATUSES.map(s => {
          const count = applications.filter(a => a.status === s).length;
          if (!count) return null;
          return (
            <button key={s}
              onClick={() => setStatusFilter(statusFilter === s ? 'all' : s)}
              className={`px-3 py-1 rounded-full text-xs font-medium border transition-all ${STATUS_STYLES[s]} ${statusFilter === s ? 'ring-2 ring-offset-1 ring-offset-canvas ring-brand' : ''}`}>
              {s} ({count})
            </button>
          );
        })}
      </div>

      {/* Cards grid */}
      {loading ? (
        <div className="flex justify-center items-center h-40">
          <div className="w-8 h-8 border-3 border-brand border-t-transparent rounded-full animate-spin" />
        </div>
      ) : shown.length === 0 ? (
        <div className="bg-surface/70 backdrop-blur-xl rounded-2xl border border-contrast/10 p-16 text-center">
          <FileText className="w-12 h-12 text-ink-3 mx-auto mb-4" />
          <p className="text-ink-2 font-medium">No applications yet</p>
          <p className="text-ink-3 text-sm mt-1">Open a candidate profile and click "Add Application" to apply to a university</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {shown.map(app => (
            <div key={app.id} className="min-w-0"><Link className="block text-sm font-semibold text-brand mb-3" to={`/partner/candidates/${app.candidate_id}`}>{app.candidate_name}</Link><ApplicationTicket application={app} basePath="/partner"/></div>
          ))}
        </div>
      )}
    </div>
  );
}
